import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Autocomplete } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';

import { OptionType } from '@/modules/common/types';
import { positionOptions } from '@/modules/eventsV2/constants';

import { ModelDataType } from '..';
import { addEventModelSchema } from '../schema';

interface IProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (model: ModelDataType) => void;
  onEdit: (model: ModelDataType) => void;
  modelOptions: OptionType[];
  sizeOptions: OptionType[];
  selectedModel: ModelDataType | null;
}

const defaultFormValues = {
  modelId: '',
  position: [],
  quantity: 0,
  sizeId: '',
};

export type EventModelValues = z.infer<typeof addEventModelSchema>;

export function AddEditModelDialog({
  isOpen,
  onOpenChange,
  onAdd,
  onEdit,
  modelOptions,
  sizeOptions,
  selectedModel,
}: IProps) {
  const isEdit = selectedModel !== null;
  const form = useForm<EventModelValues>({
    resolver: zodResolver(addEventModelSchema),
    defaultValues: defaultFormValues,
  });

  const { control, handleSubmit, reset, formState: { isDirty } } = form;

  const handleClose = async (open: boolean) => {
    onOpenChange(open);
    reset(defaultFormValues);
  };

  const handleFormSubmit = async (values: ModelDataType) => {
    if (isEdit) {
      onEdit(values);
    } else {
      onAdd(values);
    }
    handleClose(false);
  };

  useEffect(() => {
    if (isEdit) {
      form.reset(selectedModel);
    } else {
      form.reset(defaultFormValues);
    }
  }, [isEdit]);

  return (
    <>
      <Dialog onOpenChange={handleClose} open={isOpen} modal={false}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Model Details' : 'Add Model'}</DialogTitle>
            <DialogDescription>Description</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleSubmit(handleFormSubmit)(event);
              }}
            >
              <div className="flex flex-col gap-4">
                <Autocomplete
                  label="Type"
                  name="modelId"
                  control={control}
                  searchable
                  placeholder="Select type"
                  options={modelOptions}
                />

                <Autocomplete
                  label="Size"
                  name="sizeId"
                  control={control}
                  searchable
                  placeholder="Select size"
                  options={sizeOptions}
                />

                <MultiSelect
                  name="position"
                  label="Position"
                  control={control}
                  placeholder="Select position"
                  options={positionOptions}
                  maxCount={3}
                />

                <FormField
                  control={control}
                  name="quantity"
                  render={({ field, formState: { errors } }) => (
                    <FormItem>
                      <FormLabel>Quantity</FormLabel>

                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter quantity"
                          {...field}
                        />
                      </FormControl>

                      {!errors?.quantity && (
                        <p className="text-xs text-muted-foreground">
                          Set value from 1 to 100
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-button">
                    Cancel
                  </Button>
                </DialogClose>

                <Button
                  type="submit"
                  data-testid="save-button"
                  disabled={!isDirty}
                >
                  {isEdit ? 'Save' : 'Add model'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
